import React from "react";
import { Block, flattenIOPS, IOPS } from "../../models/block";
import { ROOT_BLOCK_ID } from "../../models/block";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { getCompactNumberStr } from "../../utils/iops-utils";
import { ContentTypeChip, CONTENT_TYPE_MAP } from "./ContentTypeChip";

interface BlockInfoDisplayProps {
  block: Block;
  title: string;
  currentInputIOPS?: IOPS;
  currentOutputIOPS?: IOPS;
}

// Deterministic color for each op-content key
const IOPS_COLORS: { [key: string]: string } = {
  "read-static": "#1976d2",
  "read-dynamic": "#43a047",
  "read-static_media": "#81c784",
  "write-dynamic": "#ffa000",
  "write-static": "#e53935",
  "write-static_media": "#ffcc80",
};

function getIOPSColor(key: string) {
  if (CONTENT_TYPE_MAP[key]) return CONTENT_TYPE_MAP[key].color;
  if (IOPS_COLORS[key]) return IOPS_COLORS[key];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue},70%,80%)`;
}

/** Returns a status color based on how close current is to capacity */
function getUtilizationColor(current: number, capacity: number): string {
  if (capacity <= 0) return "#9e9e9e";
  const ratio = current / capacity;
  if (ratio >= 1) return "#e53935";    // red — at or over capacity
  if (ratio >= 0.8) return "#ff9800";  // orange — nearing capacity
  if (ratio >= 0.5) return "#fbc02d";  // yellow — moderate load
  return "#43a047";                     // green — healthy
}

// ── Progress bar row (used for non-root blocks) ──────────────────────────────

const IOPSUsageBar = ({ label, current, capacity }: { label: string; current: number; capacity: number }) => {
  const percentage = capacity > 0 ? Math.min((current / capacity) * 100, 100) : 0;
  const color = getIOPSColor(label);

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, alignItems: "center" }}>
        <ContentTypeChip typeKey={label} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {getCompactNumberStr(current)} / {getCompactNumberStr(capacity)} ({percentage.toFixed(1)}%)
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          backgroundColor: "#e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${percentage}%`,
            backgroundColor: color,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};

// ── Raw IOPS row with color indicator (used for root/user block) ─────────────

const IOPSRow = ({ label, current, capacity }: { label: string; current: number; capacity: number }) => {
  const labelColor = getIOPSColor(label);
  const statusColor = capacity > 0 ? getUtilizationColor(current, capacity) : labelColor;

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
      <ContentTypeChip typeKey={label} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: statusColor,
          minWidth: 45,
          textAlign: "right",
        }}
      >
        {getCompactNumberStr(current)}
      </Typography>
    </Box>
  );
};

// ── Capacity section with progress bars (non-root blocks) ────────────────────

const IOPSCapacitySection = ({
  title,
  currentIOPS,
  capacityIOPS,
}: {
  title: string;
  currentIOPS: IOPS | undefined;
  capacityIOPS: IOPS;
}) => {
  // Always show the section if there's capacity — even when no traffic is flowing
  if (capacityIOPS.total === 0) return null;

  const flatCurrent = currentIOPS ? flattenIOPS(currentIOPS) : { total: 0 };
  const flatCapacity = flattenIOPS(capacityIOPS);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      {Object.keys(flatCapacity).filter(k => k !== "total").map((key) => {
        const currentVal = (flatCurrent as any)[key] as number ?? 0;
        const capacityVal = flatCapacity[key] as number;
        return (
          <IOPSUsageBar
            key={key}
            label={key}
            current={currentVal}
            capacity={capacityVal}
          />
        );
      })}
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
        Total: {getCompactNumberStr(flatCurrent.total)} / {getCompactNumberStr(flatCapacity.total)}
      </Typography>
    </Box>
  );
};

// ── Raw IOPS section (root/user block) ───────────────────────────────────────

const IOPSRawSection = ({
  title,
  iops,
}: {
  title: string;
  iops: IOPS | undefined;
}) => {
  if (!iops || iops.total === 0) return null;

  const flat = flattenIOPS(iops);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {Object.keys(flat)
        .filter((k) => k !== "total")
        .map((key) => {
          const val = flat[key] as number ?? 0;
          return (
            <IOPSRow
              key={key}
              label={key}
              current={val}
              capacity={0}
            />
          );
        })}
      {/* Total row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          pt: 0.5,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 88 }}>
          Total
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, minWidth: 45, textAlign: "right" }}
        >
          {getCompactNumberStr(flat.total)}
        </Typography>
      </Box>
    </Box>
  );
};

export const BlockInfoDisplay: React.FC<BlockInfoDisplayProps> = ({ block, title, currentInputIOPS, currentOutputIOPS }) => {
  const isRootBlock = block.id === ROOT_BLOCK_ID;

  return (
    <Card
      style={{
        marginLeft: 12,
        minWidth: 250,
        background: "#f7f7fa",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        fontSize: 12,
      }}
      elevation={2}
    >
      <CardContent style={{ padding: 12 }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
          {title}
        </Typography>

        {isRootBlock ? (
          /* Root/User block: show raw output IOPS values */
          <IOPSRawSection
            title="Output TPS"
            iops={currentOutputIOPS}
          />
        ) : (
          /* Non-root blocks: show capacity with progress bars */
          <>
            <IOPSCapacitySection
              title="Input Capacity"
              currentIOPS={currentInputIOPS}
              capacityIOPS={block.inputCapacity}
            />

            {block.processingCapacity && block.processingCapacity.total > 0 && (
              <IOPSCapacitySection
                title="Processing Capacity"
                currentIOPS={currentInputIOPS}
                capacityIOPS={block.processingCapacity}
              />
            )}
          </>
        )}

        {block.dynamicProperties && Object.keys(block.dynamicProperties).length > 0 && (
          <Box mt={1}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Dynamic Properties
            </Typography>
            <Box sx={{ pl: 1 }}>
              {Object.entries(block.dynamicProperties).map(([key, value]) => (
                <div key={key}>
                  <span style={{ fontWeight: 500 }}>{key}:</span> <br /> <span>{JSON.stringify(value)}</span>
                </div>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
